
import { useState, useEffect, useRef, useCallback } from 'react';
import { ProcessDefinition, AppNode, Edge } from '../types';

const AUTOSAVE_KEY = 'peak_workflow_autosave';
const DEBOUNCE_MS = 2000;

export interface AutoSavePayload {
    version: number;
    timestamp: number;
    activeProcessId: string;
    processes: ProcessDefinition[];
}

export const useAutoSave = (
    processes: ProcessDefinition[],
    activeProcessId: string,
    nodes: AppNode[],
    edges: Edge[],
    onAutoSave?: (payload: AutoSavePayload) => void
) => {
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initialMount = useRef(true);

    const performSave = useCallback(() => {
        try {
            // Construct full project state by merging current editor state into processes list
            const currentSnapshot = processes.map(p =>
                p.id === activeProcessId ? { ...p, nodes, edges } : p
            );

            const payload: AutoSavePayload = {
                version: 1,
                timestamp: Date.now(),
                activeProcessId,
                processes: currentSnapshot
            };

            // 1. Local Storage Backup (Always happens as a safety net)
            localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload));
            
            // 2. External Callback (If provided by host app)
            if (onAutoSave) {
                onAutoSave(payload);
            }

            setLastSaved(new Date());
            setIsSaving(false);
        } catch (e) {
            console.error("Auto-save failed", e);
            setIsSaving(false);
        }
    }, [processes, activeProcessId, nodes, edges, onAutoSave]);

    useEffect(() => {
        // Skip first render/mount to avoid saving default state immediately
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsSaving(true); 
        
        timeoutRef.current = setTimeout(performSave, DEBOUNCE_MS);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [processes, activeProcessId, nodes, edges, performSave]);

    return { isSaving, lastSaved };
};

export const loadAutoSave = (): { processes: ProcessDefinition[], activeProcessId: string } | null => {
    try {
        const stored = localStorage.getItem(AUTOSAVE_KEY);
        if (!stored) return null;
        
        const data = JSON.parse(stored);
        // Simple validation
        if (data && Array.isArray(data.processes) && data.activeProcessId) {
            return {
                processes: data.processes,
                activeProcessId: data.activeProcessId
            };
        }
    } catch (e) {
        console.error("Failed to load auto-save", e);
    }
    return null;
};
